import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  Avatar,
  Chip,
  Divider,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  Person as PersonIcon,
  Edit as EditIcon,
  ChildCare as ChildIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  Email as EmailIcon,
  Work as WorkIcon,
  ContactEmergency as EmergencyIcon,
  ArrowBack as ArrowBackIcon,
  Save as SaveIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { auth, db } from '../../firebase';
import UserDataForm from '../shared/UserDataForm';
import ChildrenDataForm from '../shared/ChildrenDataForm';

const COLORS = {
  primary: '#A8E6CF',
  secondary: '#88D8A3',
  accent: '#68C683',
  background: '#F8FDF8',
  surface: '#FFFFFF',
  text: {
    primary: '#2C3E50',
    secondary: '#7F8C8D',
  }
};

const UserProfile = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [children, setChildren] = useState([]);
  const [editUserOpen, setEditUserOpen] = useState(false);
  const [editChildrenOpen, setEditChildrenOpen] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchUserData();
  }, []);

  const fetchUserData = async () => {
    try {
      setLoading(true);
      const user = auth.currentUser;

      if (!user) {
        navigate('/login');
        return;
      }

      // Obtener datos del usuario
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      if (userDoc.exists()) {
        setUserData(userDoc.data());
      }

      // Obtener datos de los hijos
      const childrenQuery = query(
        collection(db, 'children'),
        where('parentId', '==', user.uid)
      );
      const childrenSnapshot = await getDocs(childrenQuery);
      const childrenData = [];
      childrenSnapshot.forEach(doc => {
        childrenData.push({ id: doc.id, ...doc.data() });
      });
      setChildren(childrenData);

    } catch (error) {
      console.error('Error fetching user data:', error);
      setError('Error al cargar los datos del perfil');
    } finally {
      setLoading(false);
    }
  };

  const handleUserDataUpdate = (newUserData) => {
    setUserData(newUserData);
    setEditUserOpen(false);
    fetchUserData(); // Recargar datos
  };

  const handleChildrenDataUpdate = (newChildren) => {
    setChildren(newChildren);
    setEditChildrenOpen(false);
    fetchUserData(); // Recargar datos
  };

  const formatDate = (date) => {
    if (!date) return 'No especificada';

    try {
      // Si es un timestamp de Firestore
      if (date.toDate) {
        return date.toDate().toLocaleDateString('es-ES');
      }

      // Si es un string de fecha (formato común: YYYY-MM-DD, DD/MM/YYYY, etc.)
      if (typeof date === 'string') {
        // Intentar diferentes formatos de string
        const dateObj = new Date(date);
        if (!isNaN(dateObj.getTime())) {
          return dateObj.toLocaleDateString('es-ES');
        }

        // Si el formato directo no funciona, intentar parsing manual para DD/MM/YYYY
        const ddmmyyyy = date.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
        if (ddmmyyyy) {
          const [, day, month, year] = ddmmyyyy;
          const parsedDate = new Date(year, month - 1, day);
          if (!isNaN(parsedDate.getTime())) {
            return parsedDate.toLocaleDateString('es-ES');
          }
        }

        // Si ya está en formato legible, devolverlo tal como está
        return date;
      }

      // Si es un objeto Date
      if (date instanceof Date) {
        return date.toLocaleDateString('es-ES');
      }

      // Si es un número (timestamp)
      if (typeof date === 'number') {
        return new Date(date).toLocaleDateString('es-ES');
      }

      return 'Fecha inválida';
    } catch (error) {
      console.error('Error formatting date:', error, date);
      return typeof date === 'string' ? date : 'Fecha inválida';
    }
  };

  const getGradeLabel = (grade) => {
    if (!grade) return 'Curso no especificado';

    const gradeLabels = {
      'infantil-3': 'Infantil 3 años',
      'infantil-4': 'Infantil 4 años',
      'infantil-5': 'Infantil 5 años',
      '1-primaria': '1º Primaria',
      '2-primaria': '2º Primaria',
      '3-primaria': '3º Primaria',
      '4-primaria': '4º Primaria',
      '5-primaria': '5º Primaria',
      '6-primaria': '6º Primaria',
      '1-eso': '1º ESO',
      '2-eso': '2º ESO',
      '3-eso': '3º ESO',
      '4-eso': '4º ESO'
    };

    // Buscar por clave exacta primero
    if (gradeLabels[grade]) {
      return gradeLabels[grade];
    }

    // Si no encuentra la clave exacta, intentar normalizar valores comunes
    const normalizedGrade = grade.toString().toLowerCase();

    // Mapear algunos valores que podrían venir de la base de datos
    if (normalizedGrade.includes('infantil')) {
      if (normalizedGrade.includes('3')) return 'Infantil 3 años';
      if (normalizedGrade.includes('4')) return 'Infantil 4 años';
      if (normalizedGrade.includes('5')) return 'Infantil 5 años';
      return 'Infantil';
    }

    if (normalizedGrade.includes('primaria')) {
      for (let i = 1; i <= 6; i++) {
        if (normalizedGrade.includes(i.toString())) {
          return `${i}º Primaria`;
        }
      }
      return 'Primaria';
    }

    if (normalizedGrade.includes('eso')) {
      for (let i = 1; i <= 4; i++) {
        if (normalizedGrade.includes(i.toString())) {
          return `${i}º ESO`;
        }
      }
      return 'ESO';
    }

    // Si no se puede mapear, devolver el valor original
    return grade;
  };

  const getRelationshipLabel = (relationship) => {
    const relationships = {
      'padre': 'Padre',
      'madre': 'Madre',
      'tutor': 'Tutor/a',
      'abuelo': 'Abuelo/a',
      'otro': 'Otro'
    };
    return relationships[relationship] || relationship;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress sx={{ color: COLORS.accent }} size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: `linear-gradient(to bottom, ${COLORS.background}, ${COLORS.surface})`, py: { xs: 2, sm: 3 } }}>
      <Container maxWidth="lg">
        {/* Header con animación */}
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          mb: { xs: 2, sm: 4 },
          animation: 'fadeInDown 0.5s ease-in-out',
          '@keyframes fadeInDown': {
            '0%': {
              opacity: 0,
              transform: 'translateY(-20px)'
            },
            '100%': {
              opacity: 1,
              transform: 'translateY(0)'
            }
          }
        }}>
          <IconButton
            onClick={() => navigate('/portal')}
            sx={{
              mr: 2,
              color: COLORS.accent,
              backgroundColor: COLORS.primary + '30',
              '&:hover': {
                backgroundColor: COLORS.primary + '50',
                transform: 'scale(1.05)',
                transition: 'all 0.3s ease'
              }
            }}
            size={isMobile ? "small" : "medium"}
          >
            <ArrowBackIcon />
          </IconButton>
          <Box>
            <Typography
              variant={isMobile ? "h5" : "h4"}
              sx={{
                fontWeight: 'bold',
                color: COLORS.text.primary,
                mb: 0.5
              }}
            >
              👤 Mi Perfil
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Gestiona tu información personal y la de tus hijos
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
            {error}
          </Alert>
        )}

        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }}>
          {/* Datos Personales con diseño mejorado */}
          <Grid item xs={12} md={6}>
            <Card sx={{
              height: '100%',
              borderRadius: 4,
              background: `linear-gradient(135deg, ${COLORS.surface} 0%, ${COLORS.background} 100%)`,
              border: `2px solid ${COLORS.primary}40`,
              boxShadow: `0 8px 24px ${COLORS.accent}20`,
              position: 'relative',
              overflow: 'hidden',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              animation: 'fadeInLeft 0.6s ease-in-out',
              '@keyframes fadeInLeft': {
                '0%': {
                  opacity: 0,
                  transform: 'translateX(-30px)'
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateX(0)'
                }
              },
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 32px ${COLORS.accent}30`
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '4px',
                background: `linear-gradient(to right, ${COLORS.accent}, ${COLORS.primary})`
              }
            }}>
              <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  justifyContent: 'space-between',
                  mb: 3,
                  pb: 2,
                  borderBottom: `2px solid ${COLORS.primary}30`
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 0 } }}>
                    <Avatar
                      sx={{
                        background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.primary})`,
                        color: 'white',
                        width: { xs: 56, sm: 64 },
                        height: { xs: 56, sm: 64 },
                        mr: 2,
                        boxShadow: `0 4px 12px ${COLORS.accent}40`
                      }}
                    >
                      <PersonIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />
                    </Avatar>
                    <Box>
                      <Typography
                        variant={isMobile ? "h6" : "h5"}
                        sx={{ fontWeight: 'bold', color: COLORS.text.primary }}
                      >
                        Datos Personales
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {isMobile ? "Contacto y personal" : "Información de contacto y personal"}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    onClick={() => setEditUserOpen(true)}
                    sx={{
                      color: COLORS.accent,
                      backgroundColor: COLORS.primary + '30',
                      alignSelf: { xs: 'flex-end', sm: 'center' },
                      '&:hover': {
                        backgroundColor: COLORS.primary + '50',
                        transform: 'rotate(15deg) scale(1.1)',
                        transition: 'all 0.3s ease'
                      }
                    }}
                    size={isMobile ? "small" : "medium"}
                  >
                    <EditIcon />
                  </IconButton>
                </Box>

                {userData ? (
                  <List sx={{ py: 0, '& .MuiListItem-root': { transition: 'all 0.3s ease' } }}>
                    <ListItem sx={{
                      px: 0,
                      py: 1.5,
                      borderRadius: 2,
                      '&:hover': {
                        backgroundColor: COLORS.primary + '15',
                        transform: 'translateX(8px)'
                      }
                    }}>
                      <ListItemIcon>
                        <Box sx={{
                          backgroundColor: COLORS.accent + '20',
                          borderRadius: 2,
                          p: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <PersonIcon sx={{ color: COLORS.accent, fontSize: 24 }} />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary="Nombre Completo"
                        secondary={`${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'No especificado'}
                        primaryTypographyProps={{ fontWeight: 'bold', color: COLORS.text.primary }}
                        secondaryTypographyProps={{ fontWeight: 500 }}
                      />
                    </ListItem>

                    <ListItem sx={{
                      px: 0,
                      py: 1.5,
                      borderRadius: 2,
                      '&:hover': {
                        backgroundColor: COLORS.primary + '15',
                        transform: 'translateX(8px)'
                      }
                    }}>
                      <ListItemIcon>
                        <Box sx={{
                          backgroundColor: COLORS.secondary + '20',
                          borderRadius: 2,
                          p: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <EmailIcon sx={{ color: COLORS.secondary, fontSize: 24 }} />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary="Email"
                        secondary={userData.email || 'No especificado'}
                        primaryTypographyProps={{ fontWeight: 'bold', color: COLORS.text.primary }}
                        secondaryTypographyProps={{ fontWeight: 500 }}
                      />
                    </ListItem>

                    <ListItem sx={{
                      px: 0,
                      py: 1.5,
                      borderRadius: 2,
                      '&:hover': {
                        backgroundColor: COLORS.primary + '15',
                        transform: 'translateX(8px)'
                      }
                    }}>
                      <ListItemIcon>
                        <Box sx={{
                          backgroundColor: COLORS.success + '20',
                          borderRadius: 2,
                          p: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <PhoneIcon sx={{ color: COLORS.success, fontSize: 24 }} />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary="Teléfono"
                        secondary={userData.phone || 'No especificado'}
                        primaryTypographyProps={{ fontWeight: 'bold', color: COLORS.text.primary }}
                        secondaryTypographyProps={{ fontWeight: 500 }}
                      />
                    </ListItem>

                    <ListItem sx={{
                      px: 0,
                      py: 1.5,
                      borderRadius: 2,
                      '&:hover': {
                        backgroundColor: COLORS.primary + '15',
                        transform: 'translateX(8px)'
                      }
                    }}>
                      <ListItemIcon>
                        <Box sx={{
                          backgroundColor: COLORS.accent + '20',
                          borderRadius: 2,
                          p: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <HomeIcon sx={{ color: COLORS.accent, fontSize: 24 }} />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary="Dirección"
                        secondary={
                          userData.address
                            ? `${userData.address}${userData.city ? `, ${userData.city}` : ''}${userData.postalCode ? ` (${userData.postalCode})` : ''}`
                            : 'No especificada'
                        }
                        primaryTypographyProps={{ fontWeight: 'bold', color: COLORS.text.primary }}
                        secondaryTypographyProps={{ fontWeight: 500 }}
                      />
                    </ListItem>

                    <ListItem sx={{
                      px: 0,
                      py: 1.5,
                      borderRadius: 2,
                      '&:hover': {
                        backgroundColor: COLORS.primary + '15',
                        transform: 'translateX(8px)'
                      }
                    }}>
                      <ListItemIcon>
                        <Box sx={{
                          backgroundColor: COLORS.primary + '40',
                          borderRadius: 2,
                          p: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          <PersonIcon sx={{ color: COLORS.accent, fontSize: 24 }} />
                        </Box>
                      </ListItemIcon>
                      <ListItemText
                        primary="Relación"
                        secondary={getRelationshipLabel(userData.relationship) || 'No especificada'}
                        primaryTypographyProps={{ fontWeight: 'bold', color: COLORS.text.primary }}
                        secondaryTypographyProps={{ fontWeight: 500 }}
                      />
                    </ListItem>

                    {userData.occupation && (
                      <ListItem sx={{
                        px: 0,
                        py: 1.5,
                        borderRadius: 2,
                        '&:hover': {
                          backgroundColor: COLORS.primary + '15',
                          transform: 'translateX(8px)'
                        }
                      }}>
                        <ListItemIcon>
                          <Box sx={{
                            backgroundColor: COLORS.secondary + '20',
                            borderRadius: 2,
                            p: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <WorkIcon sx={{ color: COLORS.secondary, fontSize: 24 }} />
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          primary="Profesión"
                          secondary={userData.occupation}
                          primaryTypographyProps={{ fontWeight: 'bold', color: COLORS.text.primary }}
                          secondaryTypographyProps={{ fontWeight: 500 }}
                        />
                      </ListItem>
                    )}

                    {(userData.emergencyContact || userData.emergencyPhone) && (
                      <ListItem sx={{
                        px: 0,
                        py: 1.5,
                        borderRadius: 2,
                        '&:hover': {
                          backgroundColor: COLORS.primary + '15',
                          transform: 'translateX(8px)'
                        }
                      }}>
                        <ListItemIcon>
                          <Box sx={{
                            backgroundColor: '#EF5350' + '20',
                            borderRadius: 2,
                            p: 1,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}>
                            <EmergencyIcon sx={{ color: '#EF5350', fontSize: 24 }} />
                          </Box>
                        </ListItemIcon>
                        <ListItemText
                          primary="Contacto de Emergencia"
                          secondary={`${userData.emergencyContact || 'No especificado'}${userData.emergencyPhone ? ` - ${userData.emergencyPhone}` : ''}`}
                          primaryTypographyProps={{ fontWeight: 'bold', color: COLORS.text.primary }}
                          secondaryTypographyProps={{ fontWeight: 500 }}
                        />
                      </ListItem>
                    )}
                  </List>
                ) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
                    No hay datos personales disponibles
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Datos de Hijos con diseño mejorado */}
          <Grid item xs={12} md={6}>
            <Card sx={{
              height: '100%',
              borderRadius: 4,
              background: `linear-gradient(135deg, ${COLORS.surface} 0%, ${COLORS.background} 100%)`,
              border: `2px solid ${COLORS.secondary}40`,
              boxShadow: `0 8px 24px ${COLORS.secondary}20`,
              position: 'relative',
              overflow: 'hidden',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              animation: 'fadeInRight 0.6s ease-in-out',
              '@keyframes fadeInRight': {
                '0%': {
                  opacity: 0,
                  transform: 'translateX(30px)'
                },
                '100%': {
                  opacity: 1,
                  transform: 'translateX(0)'
                }
              },
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: `0 12px 32px ${COLORS.secondary}30`
              },
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '4px',
                background: `linear-gradient(to right, ${COLORS.secondary}, ${COLORS.accent})`
              }
            }}>
              <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', sm: 'row' },
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  justifyContent: 'space-between',
                  mb: 3,
                  pb: 2,
                  borderBottom: `2px solid ${COLORS.secondary}30`
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 0 } }}>
                    <Avatar
                      sx={{
                        background: `linear-gradient(135deg, ${COLORS.secondary}, ${COLORS.accent})`,
                        color: 'white',
                        width: { xs: 56, sm: 64 },
                        height: { xs: 56, sm: 64 },
                        mr: 2,
                        boxShadow: `0 4px 12px ${COLORS.secondary}40`
                      }}
                    >
                      <ChildIcon sx={{ fontSize: { xs: 28, sm: 32 } }} />
                    </Avatar>
                    <Box>
                      <Typography
                        variant={isMobile ? "h6" : "h5"}
                        sx={{ fontWeight: 'bold', color: COLORS.text.primary }}
                      >
                        Mis Hijos
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        {isMobile ? "Menores a cargo" : "Información de los menores a cargo"}
                      </Typography>
                    </Box>
                  </Box>
                  <IconButton
                    onClick={() => setEditChildrenOpen(true)}
                    sx={{
                      color: COLORS.secondary,
                      backgroundColor: COLORS.secondary + '30',
                      alignSelf: { xs: 'flex-end', sm: 'center' },
                      '&:hover': {
                        backgroundColor: COLORS.secondary + '50',
                        transform: 'rotate(15deg) scale(1.1)',
                        transition: 'all 0.3s ease'
                      }
                    }}
                    size={isMobile ? "small" : "medium"}
                  >
                    <EditIcon />
                  </IconButton>
                </Box>

                {children.length > 0 ? (
                  <Box>
                    {children.map((child, index) => (
                      <Box key={child.id || index} sx={{ mb: 2.5 }}>
                        <Paper
                          elevation={3}
                          sx={{
                            p: 3,
                            background: `linear-gradient(135deg, ${COLORS.primary}15 0%, ${COLORS.background} 100%)`,
                            border: `2px solid ${COLORS.primary}50`,
                            borderRadius: 3,
                            position: 'relative',
                            overflow: 'hidden',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateX(8px)',
                              boxShadow: `0 8px 20px ${COLORS.primary}40`
                            },
                            '&::before': {
                              content: '""',
                              position: 'absolute',
                              left: 0,
                              top: 0,
                              bottom: 0,
                              width: '4px',
                              background: `linear-gradient(to bottom, ${COLORS.secondary}, ${COLORS.accent})`
                            }
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                            <Avatar sx={{
                              background: `linear-gradient(135deg, ${COLORS.secondary}, ${COLORS.primary})`,
                              width: 40,
                              height: 40,
                              fontWeight: 'bold',
                              fontSize: '1.2rem'
                            }}>
                              {(child.nombre || child.firstName)?.charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography variant="h6" sx={{ fontWeight: 'bold', color: COLORS.text.primary }}>
                              {child.nombre || child.firstName} {child.apellidos || child.lastName}
                            </Typography>
                          </Box>

                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, mb: 2.5 }}>
                            <Chip
                              label={getGradeLabel(child.curso || child.grade)}
                              size="small"
                              sx={{
                                background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.secondary})`,
                                color: 'white',
                                fontWeight: 'bold',
                                boxShadow: `0 2px 8px ${COLORS.accent}30`
                              }}
                            />
                            {(child.clase || child.classroom) && (
                              <Chip
                                label={`📚 ${child.clase || child.classroom}`}
                                size="small"
                                sx={{
                                  border: `2px solid ${COLORS.primary}`,
                                  fontWeight: 'bold',
                                  backgroundColor: COLORS.surface
                                }}
                                variant="outlined"
                              />
                            )}
                          </Box>

                          <Box sx={{ pl: 1.5, borderLeft: `3px solid ${COLORS.primary}40` }}>
                            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, fontWeight: 500 }}>
                              🎂 <strong>Fecha de nacimiento:</strong> {formatDate(child.fechaNacimiento || child.birthDate)}
                            </Typography>

                            {(child.alergias || child.allergies) && (
                              <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500, color: '#EF5350' }}>
                                ⚠️ <strong>Alergias:</strong> {child.alergias || child.allergies}
                              </Typography>
                            )}

                            {(child.condicionesMedicas || child.medicalConditions) && (
                              <Typography variant="body2" sx={{ mb: 1.5, fontWeight: 500, color: '#FF9800' }}>
                                🏥 <strong>Condiciones médicas:</strong> {child.condicionesMedicas || child.medicalConditions}
                              </Typography>
                            )}

                            {(child.notas || child.notes) && (
                              <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                                📝 <strong>Notas:</strong> {child.notas || child.notes}
                              </Typography>
                            )}
                          </Box>
                        </Paper>
                        {index < children.length - 1 && (
                          <Divider sx={{
                            my: 2,
                            borderColor: COLORS.secondary + '30',
                            borderStyle: 'dashed'
                          }} />
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Paper
                    elevation={0}
                    sx={{
                      p: 4,
                      textAlign: 'center',
                      backgroundColor: COLORS.background,
                      borderRadius: 3,
                      border: `2px dashed ${COLORS.secondary}40`
                    }}
                  >
                    <ChildIcon sx={{ fontSize: 60, color: COLORS.text.secondary, mb: 2, opacity: 0.5 }} />
                    <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                      No hay hijos registrados
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Haz clic en el botón de editar para añadir información de tus hijos
                    </Typography>
                  </Paper>
                )}
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Dialog para editar datos de usuario */}
        <Dialog
          open={editUserOpen}
          onClose={() => setEditUserOpen(false)}
          maxWidth="md"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant={isMobile ? "subtitle1" : "h6"}>Editar Datos Personales</Typography>
            <IconButton onClick={() => setEditUserOpen(false)} size={isMobile ? "small" : "medium"}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <UserDataForm
              initialData={userData}
              onComplete={handleUserDataUpdate}
            />
          </DialogContent>
        </Dialog>

        {/* Dialog para editar datos de hijos */}
        <Dialog
          open={editChildrenOpen}
          onClose={() => setEditChildrenOpen(false)}
          maxWidth="lg"
          fullWidth
          fullScreen={isMobile}
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant={isMobile ? "subtitle1" : "h6"}>Editar Datos de Hijos</Typography>
            <IconButton onClick={() => setEditChildrenOpen(false)} size={isMobile ? "small" : "medium"}>
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent sx={{ p: 0 }}>
            <ChildrenDataForm
              initialChildren={children}
              onComplete={handleChildrenDataUpdate}
            />
          </DialogContent>
        </Dialog>
      </Container>
    </Box>
  );
};

export default UserProfile;